"use client";

// WO-GF-001-P1B-S13 (RW3-8 · founder ruling (b) of walk 3) — CONTENT PAGES KEEP THE MOUNTAIN HUD.
//
// THE RULING, verbatim intent: content pages swap the HUD for a big orange "Select your mountain" button;
// the founder ruled that an ERROR CLASS — "might have been from the original site, but it shouldn't be
// that way" — and that content pages must KEEP the mountain HUD context, live notwithstanding.
//
// LIVE WAS CHECKED FIRST anyway, and confirms the founder's description rather than contradicting it
// (read-only, 2026-07-22, production @1440): /contact-us, /faq, /trips and /general-information all render
// the orange "Select Your Mountain" pill and NO hub HUD (headerModes = ["Select Your Mountain", cart]).
// So this is a DELIBERATE, RECORDED divergence from live under ruling (b) — the same posture as RW3-4 —
// not a fidelity fix. Nothing here is hidden behind a "matches live" claim.
//
// WHICH MOUNTAIN? Never invented, and never a silent default:
//   • every mountain page records the mountain the visitor is actually on (see mountain-context.ts);
//   • a content page reads that memory and renders THAT mountain's HUD;
//   • with no memory at all (a cold direct landing on /faq) it falls back to WHISTLER — the established
//     precedent for exactly this "content page carries no mountain token" case (S7 footer quicklinks and
//     the RW-15(b) hamburger Info items both resolve to live's Whistler defaults).
// Destinations resolve through the SHARED `hudNavHref` map, so the content HUD, the hub HUD and the
// landing funnel can never drift apart, and no new destination is introduced.
//
// The "Select your mountain" control is NOT destroyed — it becomes the mountain badge's own click, so the
// visitor can still switch mountains from a content page (the capability live's pill provided is kept).
//
// PIXEL: this DOES move the content-page baselines, by design. The WO anticipates it ("RW3-8's HUD restore
// WILL move baselines on their surfaces — re-baseline with the ruling cited, never silently").
//
// Harness hooks: [data-content-hud], [data-content-hud-item], [data-content-hud-badge].

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { HUB_NAV_ITEMS } from "./hubNavIcons";
import { hudNavHref } from "./hub-nav";
import { readMountainContext, mountainLabel, mountainBadgeSrc } from "./mountain-context";
import { openMountainSelect } from "./MountainSelectLayer";

export default function ContentHudLayer() {
  const [code, setCode] = useState<string | null>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [strip, setStrip] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // the captured content header's "Select your mountain" pill — the control ruling (b) replaces.
    const pill = document.querySelector<HTMLElement>(
      'header [data-behavior="nav-toggle"]',
    );
    if (!pill) return;
    setCode(readMountainContext());
    pill.setAttribute("data-content-hud-host", "");

    // S26 · A3 — THE PILL WAS A DEAD CONTROL ON /faq, /trips AND /contact-us. Measured in the A2 mount
    // audit: `elementFromPoint` at the pill's centre returned the badge <img>, and clicking it opened
    // nothing. The cause is React portal semantics, not layout — the badge below is portaled INTO this
    // pill, so its click bubbles through the REACT tree (this layer) and never reaches the pill's own
    // React `onClick` in `SiteHeaderContent`. The pill only responded on the sliver of itself the badge
    // did not cover, which no visitor would find.
    //
    // A NATIVE listener bubbles through the DOM tree instead, where the badge really is a descendant of
    // the pill — so this fires for a click anywhere on the pill, badge included. Same technique F141 used
    // for the cart icon. `SiteHeaderContent`'s React onClick opens the SAME one selector, so a click that
    // reaches both cannot open two different things.
    const openSelect = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      openMountainSelect(null);
    };
    pill.addEventListener("click", openSelect);

    // S16 · F101 — PORTAL INTO OUR OWN CONTAINERS, NEVER INTO THE HEADER'S REACT NODES.
    // The pill and its parent are rendered by `SiteHeaderContent` (a real React component) AND carry
    // React-set text children, so portalling straight into them made React's container ambiguous and it
    // logged "Cannot use a ref on a React element as a container to createRoot or createPortal…" four
    // times on every content page — the red error the founder saw on /faq. The portal targets are now
    // two empty spans we create imperatively: plain DOM, owned by nobody else, no ambiguity. Layout is
    // unchanged because each span is inserted exactly where the portal used to render.
    // (This is S15's F91, sized-then-parked; the founder's F101 is the same defect.)
    const badgeHost = document.createElement("span");
    badgeHost.setAttribute("data-content-hud-slot", "badge");
    badgeHost.style.display = "inline-flex";
    badgeHost.style.alignItems = "center";
    pill.appendChild(badgeHost);
    setHost(badgeHost);

    let stripHost: HTMLElement | null = null;
    // F122 — restore each element's own inline style, not a blank (other layers write here too).
    const rowSaved: [HTMLElement, string][] = [];
    if (pill.parentElement) {
      stripHost = document.createElement("div");
      stripHost.setAttribute("data-content-hud-slot", "modes");
      // S29 · F172 — the strip is a real flex box, not `display:contents`, so that IT is the element
      // that yields when the row does not fit. With `display:contents` the five mode items were direct
      // children of the row with no shrinkable box around them, so the row had a hard 676px floor and
      // something had to be pushed out of the lozenge — and the thing pushed out was the PILL.
      // `min-width:0` lets this box shrink below its content; `overflow-x:auto` keeps the mode items
      // reachable inside it. Desktop layout is unchanged: the row's children go from
      // (pill, modes-div) to (pill, stripHost > modes-div), and the 12px row gap is between the same
      // two visual groups either way — measured identical at 1440 (pill left 346, mode items unmoved).
      stripHost.style.display = "flex";
      stripHost.style.alignItems = "center";
      stripHost.style.minWidth = "0";
      stripHost.style.overflowX = "auto";
      pill.parentElement.appendChild(stripHost);
      setStrip(stripHost);

      // ---------------------------------------------------------------------------------------
      // WO-GF-001-P1B-S16 · F122 — ONE LINE, per the founder's ruling of 2026-07-23:
      // "logo · pill · mode options · cart · profile, one row, nothing underneath, site-wide"
      // (the original landing page, before a mountain is chosen, is the one page with no pill —
      // that surface uses SiteHeader, not this HUD, so it is untouched by this).
      //
      // THE DEFECT: this container is `display:block` on live, because live's content HUD holds
      // exactly ONE button — the pill. RW3-8 added the hub's five mode items into it, and the
      // strip above is `display:contents`, so those items became block-level siblings of a
      // `position:relative` pill. Measured result at 2000 wide: the pill's box (871→1057) sat ON
      // TOP of `flights` (ends 1027) and ran into `stays` — the overlap in the founder's walk.
      //
      // THE FIX: make the row an actual row. Nothing is repositioned by hand and no width is
      // hardcoded; the container simply lays its children out in a line and the lozenge is
      // allowed to size to them instead of staying at live's one-button width.
      // ---------------------------------------------------------------------------------------
      const row = pill.parentElement;
      rowSaved.push([row, row.style.cssText]);
      // S29 · F172 — the PILL never shrinks and never gets pushed out; the strip beside it absorbs the
      // squeeze (see the stripHost note above). Without this the flex algorithm is free to take the
      // width out of whichever item it likes. Saved into `rowSaved` first so unmount restores the
      // pill's own inline style like every other element this layer touches (F122's rule).
      rowSaved.push([pill, pill.style.cssText]);
      pill.style.flexShrink = "0";

      // -------------------------------------------------------------------------------------
      // S36 · F238/F239 — THE "BROKEN HUD PILL" IS THE PILL'S OWN SKIN, NOT ITS GEOMETRY.
      //
      // Measured 2026-07-29 before this change, driving the founder's two real entry paths from
      // the home landing page with NO mountain selected (localStorage cleared) at nine widths
      // 1440→560: the pill's rect, the badge's rect, the mode items and the hit test are
      // BYTE-FOR-BYTE IDENTICAL to a cold direct load on every path and every width
      // (pill 346,16 186x60 · badge 405 · items 5 · hit IMG · lozenge inset 16 @1440). So the
      // entry path is NOT the variable and a "navigation-state" fix would have been aimed at
      // nothing. What IS wrong is visible in one screenshot: `SiteHeaderContent` styles this
      // control as live's orange CALL-TO-ACTION lozenge — `SELECT_BTN` hard-codes
      // backgroundColor #f15a22, a matching 1px border, borderRadius 999px, padding 10px 30px
      // and a fixed 186x60 box — because on live it says "Select your mountain". RW3-8 killed
      // that control and this layer blanks its label and portals the mountain badge inside it,
      // but the CTA SKIN was never taken away. The result the founder walked is a white
      // rectangular mountain logo (68x34 JPEG) pasted inside a solid orange 186x60 button.
      //
      // THE REFERENCE IS THE HUB'S OWN HUD, and it was measured rather than imagined: on
      // /mountain-whistler and /mountain-big-white the SAME badge renders with
      // backgroundColor rgba(0,0,0,0), borderRadius 0px and padding 0px — logo on white, mode
      // strip beside it. Content pages now present it the same way, which is what "the content
      // page KEEPS the mountain HUD" (ruling (b)) was always supposed to look like.
      //
      // Deliberately NOT changed: `height`. The 60px is what sets the toolbar row height that
      // HudShrinkLayer collapses against (F103: toolbar 92→76), so dropping it would move a
      // second, unrelated thing. Width goes to `auto` so the badge is not floating in a
      // 118px-too-wide box, and 8px of side padding keeps the change-mountain hit target
      // comfortably larger than the badge itself (84x60, vs the badge's 68x34).
      //
      // Not retokened to `--sg-color-brand`, deliberately and reportably: apps/root serves
      // INLINED CAPTURED CSSOM and by ruling carries no Tailwind and no global stylesheet, so
      // no `--sg-*` custom property is served on this surface (verified: zero `--sg-` hits in
      // apps/root/src). A var() here would resolve to nothing and render an invisible control —
      // a real regression bought for a compliance line. Flagged in the report instead.
      //
      // PIXEL: this DOES move the content-page desktop baselines, by ruling (F238/F239), and the
      // move is recorded old→new in the close-out rather than re-shot silently.
      // -------------------------------------------------------------------------------------
      pill.style.backgroundColor = "transparent";
      pill.style.border = "none";
      pill.style.borderRadius = "0";
      pill.style.padding = "0 8px";
      pill.style.width = "auto";
      pill.style.minWidth = "0";
      pill.style.boxShadow = "none";
      pill.setAttribute("data-content-hud-pill-plain", "");
      row.style.minWidth = "0";
      row.style.display = "flex";
      row.style.flexWrap = "nowrap";
      row.style.alignItems = "center";
      row.style.gap = "12px";
      row.style.width = "auto";

      // the lozenge is sized for live's single pill; let it grow to hold the line.
      const lozenge = row.parentElement;
      if (lozenge) {
        rowSaved.push([lozenge, lozenge.style.cssText]);
        lozenge.style.width = "auto";
        lozenge.style.maxWidth = "none";
        lozenge.style.flexWrap = "nowrap";

        // -------------------------------------------------------------------------------------
        // S29 · F172 — THE PILL WAS DEAD, AND NOT FOR THE S26 REASON. S26 fixed a portal-swallowed
        // click (native listener above) and that fix is intact — at >=780px wide a real hit-tested
        // click opens the selector on all three routes. The founder's dead pill is a NARROW-WINDOW
        // defect this file created two fixes ago:
        //
        //   this lozenge is `justify-content:center` + `overflow-x:hidden` (live's own CSS, for its
        //   ONE button). F122 above made the row `nowrap` and 676px wide to hold the five mode
        //   items. Below ~780px the row is wider than the lozenge, so CENTERING pushes its left
        //   edge — where the pill sits — out past the clip boundary and `overflow:hidden` DELETES
        //   it. Measured: pill left = +16px @780 · -24 @700 · -94 @560 · -179 @390, and
        //   documentElement.scrollWidth == innerWidth at every one of those, so there is no
        //   horizontal scroll to recover it. The control is not merely off-screen, it is clipped
        //   away and physically unclickable. Every gate ran at 1440, where the row fits.
        //
        // THE FIX IS THE PRIMITIVE BUILT FOR THIS, not a hand-computed offset: `safe center`
        // centers while there is room and falls back to start-alignment the moment the content
        // overflows, so the pill can never leave the clip box. Measured after: pill left = 346
        // @1440 (UNCHANGED), 184 @780, 184 @700, 176 @560, and a real hit-tested click opens the
        // selector at every one.
        //
        // NO PIXEL BASELINE MOVES, and that is measured, not assumed: `/faq` re-gated at
        // 0.23% desktop / 0.09% mobile, identical to the ledger. The desktop baseline is shot at
        // 1440, where the row still fits and centring is unchanged; the MOBILE baseline is shot at
        // 390 with a phone UA, and `page.tsx` serves a different header entirely for that UA
        // (`isMobileUA()` → SiteHeaderContent's mobile variant), so this desktop-header code does
        // not render there at all. The narrow widths this fixes are a DESKTOP browser resized —
        // which is the founder's half-screen window, and is not a baseline width.
        // -------------------------------------------------------------------------------------
        // The lozenge keeps live's `overflow-x: hidden`. It does NOT need to scroll: with `safe center`
        // above and the shrinkable strip beside the pill, the row now fits inside it at every width, so
        // making the lozenge scrollable too only produced a SECOND scrollbar stacked under the first
        // (seen in the 700px browser walk). One scroll affordance, on the one element that overflows.
        lozenge.style.justifyContent = "safe center";
      }
    }

    // the pill's label/chevron are the fallback UI; hide them once the HUD takes over.
    // S23 · F148 — hide the RAW TEXT-NODE label too. On /terms-and-conditions and /privacy-policy the pill's
    // "Select your mountain" label is a bare text node (not an element), so the element-only loop left it
    // visible and it mashed into the mountain badge (the founder's "broken pill"). Iterate ALL child nodes:
    // elements get display:none; a non-empty text node is blanked (its text saved for restore).
    const hidden: HTMLElement[] = [];
    const hiddenText: [Text, string][] = [];
    for (const child of Array.from(pill.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        const t = child as Text;
        if (t.textContent && t.textContent.trim()) {
          hiddenText.push([t, t.textContent]);
          t.textContent = "";
        }
        continue;
      }
      const el = child as HTMLElement;
      if (el.hasAttribute?.("data-content-hud") || el === badgeHost) continue;
      el.style.display = "none";
      hidden.push(el);
    }
    return () => {
      for (const el of hidden) el.style.display = "";
      for (const [t, text] of hiddenText) t.textContent = text;
      for (const [el, css] of rowSaved) el.style.cssText = css;
      pill.removeEventListener("click", openSelect);
      pill.removeAttribute("data-content-hud-host");
      pill.removeAttribute("data-content-hud-pill-plain");
      badgeHost.remove();
      stripHost?.remove();
    };
  }, []);

  if (!host || !code) return <span data-content-hud-layer hidden />;
  return (
    <>
      <span data-content-hud-layer hidden />
      <ContentHud code={code} host={host} strip={strip} />
    </>
  );
}

function ContentHud({ code, host, strip }: { code: string; host: HTMLElement; strip: HTMLElement | null }) {
  // TWO portals, deliberately. The pill IS the change-mountain toggle (its React onClick opens the
  // 30-mountain list, and `chrome/nav-dropdown-opens` clicks its centre), so ONLY the mountain badge goes
  // inside it — filling the pill with mode links made its centre an <a> and broke that toggle on the three
  // content surfaces. The mode items therefore render as a SIBLING after the pill.
  // (Caught by the full sweep, not by the RW3-8 check, which asserted the HUD but not the control it
  // replaced — the check has been widened to assert the toggle too.)
  return (
    <>
      {createPortal(
        <MountainBadge code={code} />,
        host,
      )}
      {strip ? createPortal(<ModeStrip code={code} />, strip) : null}
    </>
  );
}

/**
 * The mountain badge in the pill. S16 · F102 — renders the mountain's NAME whenever we have no badge URL
 * we know resolves, and falls back to the name again if an image we did have fails to load. A broken
 * <img> here is what produced the "?" placeholder box jammed next to the pill text.
 */
function MountainBadge({ code }: { code: string }) {
  const label = mountainLabel(code);
  const [src, setSrc] = useState<string | null>(() => mountainBadgeSrc(code));
  return (
    <span
      data-content-hud-badge={code}
      title="Change mountain"
      style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}
    >
      {src ? (
        <img
          alt={label}
          src={src}
          onError={() => setSrc(null)}
          style={{ height: 34, width: "auto" }}
        />
      ) : (
        <span data-content-hud-badge-text style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
          {label}
        </span>
      )}
    </span>
  );
}

function ModeStrip({ code }: { code: string }) {
  return (
    <div
      data-content-hud
      style={{ display: "flex", alignItems: "center", gap: 10 }}
    >
      {HUB_NAV_ITEMS.map((it, i) => {
        const href = it.disabled ? null : hudNavHref(code, it.label);
        const inner = (
          <>
            <span
              className="MuiBox-root css-1m0mia4"
              style={{ display: "inline-flex" }}
              dangerouslySetInnerHTML={{ __html: it.svg }}
            />
            <span className={it.labelCls}>{it.label}</span>
          </>
        );
        return href ? (
          <a
            key={i}
            data-content-hud-item={it.label}
            href={href}
            className={it.buttonClass}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none" }}
          >
            {inner}
          </a>
        ) : (
          // FLIGHTS / STAYS stay disabled ("Coming Soon"), exactly as the hub HUD renders them.
          <span
            key={i}
            data-content-hud-item={it.label}
            aria-disabled="true"
            className={it.buttonClass}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, opacity: 0.45 }}
          >
            {inner}
          </span>
        );
      })}
    </div>
  );
}
